import React, { useState } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  InputLabel,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { ArrowDropDown, Close } from "@mui/icons-material";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import "react-day-picker/style.css";

import { CustomInput } from "../../../../../../components/Custom/Input";
import { ALERT_DIALOG } from "../../../../../../store/constants/auth";
import { getCssVariableHandler } from "../../../../../../utils/styles/getCssVariables";
import styles from "../../sessions.module.css";
import {
  EachDateOfSessionCheckbox,
  LabelWithTbdToggle,
  OvernightCheckbox,
  ShiftTimePicker,
} from "./PositionFormFields";
import type { PositionFormik, SessionDates, ShiftTimeInfo } from "./positionForm.types";
import {
  applyCalendarSelection,
  removeShiftRow,
  withOvernight,
  withTbd,
  withTimeIn,
  withTimeOut,
} from "./positionForm.utils";

const pinkHex = () => getCssVariableHandler({ variableName: "--primary-pink-hex" });
const pinkRgb = () => getCssVariableHandler({ variableName: "--primary-pink-rgb" });
const fontRgb = () => getCssVariableHandler({ variableName: "--font-rgb" });

const DAY_PICKER_CLASSES = (() => {
  const base = getDefaultClassNames();
  return {
    month: `${base.month} ${styles.calMonth}`,
    weekday: `${base.weekday} ${styles.calWeekDays}`,
    day: `${base.day} ${styles.calDay}`,
    selected: `${base.selected} ${styles.calSelected}`,
    day_button: `${base.day_button} ${styles.calDayButton}`,
    chevron: `${base.chevron} ${styles.calChevron}`,
    month_caption: `${base.month_caption} ${styles.calMonthCaption}`,
    month_grid: `${base.month_grid} ${styles.calMonthGrid}`,
    weekdays: `${base.weekdays} ${styles.calWeekDays}`,
  };
})();

type CalendarProps = Pick<PositionFormik, "values" | "setValues" | "handleBlur"> & {
  session: SessionDates;
};

/** Updates only the rows picked on the calendar. */
const mapCalendarRows = (rows: ShiftTimeInfo[], update: (row: ShiftTimeInfo) => ShiftTimeInfo) =>
  rows?.map((row) => (row?.isSelectedFromCalendar ? update(row) : row));

/**
 * Bulk inputs under the calendar. Whatever is set here is copied to every
 * date picked on the calendar (and to dates picked afterwards).
 */
const CalendarBulkFields = ({ values, setValues, handleBlur, session }: CalendarProps) => {
  const isMidWidth = useMediaQuery("(max-width:1000px) and (min-width:930px)");
  const isNarrow = useMediaQuery("(max-width:930px)");
  const isTbd = !!values?.calendarTBD;
  // The bulk times are kept on the first day of the session.
  const sessionAnchor: Partial<ShiftTimeInfo> = { dateStart: session?.sessionStart };

  const toggleTbd = () =>
    setValues((prev) => {
      const nextTbd = !prev?.calendarTBD;
      return {
        ...prev,
        calendarTBD: nextTbd,
        calendarNumberOfHours: "",
        calendarTimeIn: "",
        calendarTimeOut: "",
        positionTimeInfo: mapCalendarRows(prev?.positionTimeInfo, (row) => withTbd(row, nextTbd)),
      };
    });

  const timeColumn = {
    width: isTbd ? "50%" : "30%",
    "@media (max-width: 600px)": { width: "100%" },
  };

  return (
    <Grid container sx={{ display: "flex", alignItems: "center", gap: "1.25rem" }}>
      <Grid item sx={timeColumn}>
        {!isTbd ? (
          <Box className={styles["form-box"]} sx={{ alignItems: "center" }}>
            <LabelWithTbdToggle label="Time In" isTbd={isTbd} onToggle={toggleTbd} />
            <ShiftTimePicker
              name="calendarTimeIn"
              value={values?.calendarTimeIn ? dayjs(values?.calendarTimeIn) : null}
              onChange={(picked) =>
                setValues((prev) => ({
                  ...prev,
                  calendarTimeIn: withTimeIn(
                    sessionAnchor,
                    picked,
                    session?.sessionStart,
                  ).timeIn,
                  positionTimeInfo: prev?.positionTimeInfo?.map((row) =>
                    row?.tbd ? row : withTimeIn(row, picked, session?.sessionStart),
                  ),
                }))
              }
            />
          </Box>
        ) : (
          <Box className={styles["form-box"]}>
            <LabelWithTbdToggle label="No. of hours" isTbd={isTbd} onToggle={toggleTbd} />
            <CustomInput
              type="number"
              fullWidth
              placeholder="10"
              name="calendarNumberOfHours"
              value={values?.calendarNumberOfHours}
              onChange={(e) => {
                const numberOfHours = e?.target?.value;
                setValues((prev) => ({
                  ...prev,
                  calendarNumberOfHours: numberOfHours,
                  positionTimeInfo: mapCalendarRows(prev?.positionTimeInfo, (row) => ({
                    ...row,
                    numberOfHours,
                  })),
                }));
              }}
            />
          </Box>
        )}
      </Grid>

      {!isTbd ? (
        <Grid item sx={timeColumn}>
          <Box className={styles["form-box"]}>
            <InputLabel>Time End</InputLabel>
            <ShiftTimePicker
              name="calendarTimeOut"
              value={values?.calendarTimeOut ? dayjs(values?.calendarTimeOut) : null}
              onChange={(picked) => {
                const endHour = picked?.hour();
                const minute = picked?.minute();
                setValues((prev) => ({
                  ...prev,
                  calendarTimeOut: withTimeOut(
                    sessionAnchor,
                    endHour,
                    minute,
                    session?.sessionStart,
                  ).timeOut,
                  positionTimeInfo: prev?.positionTimeInfo?.map((row) =>
                    row?.tbd ? row : withTimeOut(row, endHour, minute, session?.sessionStart),
                  ),
                }));
              }}
            />
          </Box>
        </Grid>
      ) : null}

      <Grid item sx={{ width: "14%" }}>
        <Box className={styles["form-box"]}>
          <InputLabel>Qty</InputLabel>
          <CustomInput
            type="number"
            fullWidth
            placeholder="10"
            name="calendarQuantity"
            value={values?.calendarQuantity}
            onBlur={handleBlur}
            onChange={(e) => {
              const quantity = e?.target?.value;
              setValues((prev) => ({
                ...prev,
                calendarQuantity: quantity,
                positionTimeInfo: prev?.positionTimeInfo?.map((row: ShiftTimeInfo) => ({
                  ...row,
                  quantity,
                })),
              }));
            }}
          />
        </Box>
      </Grid>

      <Grid item sx={{ width: isNarrow || isMidWidth ? "13%" : "11.5%" }}>
        <OvernightCheckbox
          name="calendarOvernight"
          checked={values?.calendarOvernight}
          onBlur={handleBlur}
          onChange={(overnight) =>
            setValues((prev) => ({
              ...prev,
              calendarOvernight: overnight,
              positionTimeInfo: prev?.positionTimeInfo?.map((row) =>
                withOvernight(row, overnight),
              ),
            }))
          }
        />
      </Grid>
    </Grid>
  );
};

/** Chip for one picked date, with a button to remove it. */
const SelectedDateChip = ({ row, onRemove }: { row: ShiftTimeInfo; onRemove: () => void }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "0.5rem",
      padding: "0.25rem 0.5rem",
      borderRadius: "12px",
      cursor: "pointer",
      border: "1px solid #D175B6",
      backgroundColor: pinkHex(),
    }}
  >
    <Typography component="span" sx={{ textWrap: "nowrap" }}>
      {dayjs(row?.dateStart)?.format("ddd") || "N/A"}{" "}
      {dayjs(row?.dateStart)?.format("MM/DD/YY") || "N/A"}
    </Typography>
    <Button
      onClick={onRemove}
      sx={{
        borderRadius: "50%",
        background: pinkRgb(),
        boxShadow: "0px 0px 20px 0px rgba(209, 117, 182, 0.40)",
        minWidth: "auto",
        padding: "2px !important",
        border: "2px solid",
        borderColor: "white",
      }}
    >
      <Close sx={{ fontSize: "1rem", fontWeight: 500 }} />
    </Button>
  </Box>
);

/**
 * "Select Dates to add positions" accordion: multi-date calendar, chips with the
 * picked dates, the "each date of the session" switch and the bulk inputs.
 */
const CalendarDatesPanel = ({ values, setValues, handleBlur, session }: CalendarProps) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);

  const rows: ShiftTimeInfo[] = values?.positionTimeInfo ?? [];
  const pickedRows = rows.filter((row) => row?.isSelectedFromCalendar);

  const confirmRemove = (row) =>
    dispatch({
      type: ALERT_DIALOG,
      payload: {
        open: true,
        title: "Confirmation alert!",
        message: "Are you sure you want to delete this position?",
        onOk: () =>
          setValues((prev) => {
            const current: ShiftTimeInfo[] = prev?.positionTimeInfo ?? [];
            const byIdentity = current.indexOf(row);
            const index =
              byIdentity >= 0
                ? byIdentity
                : current.findIndex(
                    (item) => item?.isSelectedFromCalendar && item?.dateStart === row?.dateStart,
                  );
            return removeShiftRow(prev, index);
          }),
      },
    });

  return (
    <Box
      sx={{
        margin: "1rem 0",
        paddingLeft: "16px",
        display: "flex",
        justifyContent: "flex-start",
        width: "100%",
        overflowX: "auto",
      }}
    >
      <Box sx={{ width: "100%", minWidth: isOpen ? "800px" : "100px", transition: "all 0.5s ease" }}>
        <Accordion
          onChange={(_, expanded) => setIsOpen(expanded)}
          sx={{
            height: "100%",
            background: "transparent",
            border: `1px solid ${pinkRgb()}`,
            borderRadius: "8px !important",
            overflow: "hidden",
          }}
        >
          <AccordionSummary
            expandIcon={
              <ArrowDropDown
                sx={{ color: fontRgb(), mx: "0.5rem", fontSize: "1.7142857142857142rem" }}
              />
            }
            sx={{
              padding: "0 0 0 10px !important",
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              backgroundColor: "transparent",
              borderTopRightRadius: "0.5rem",
              borderTopLeftRadius: "0.5rem",
              borderBottom: "none",
              "&.Mui-expanded": {
                backgroundColor: "black",
                borderBottom: "1px solid",
                borderColor: pinkHex(),
                minHeight: "3rem !important",
              },
              "& .MuiAccordionSummary-content": { margin: "0 !important" },
            }}
          >
            <Typography sx={{ fontSize: "1rem", fontWeight: "500" }}>
              Select Dates to add positions
            </Typography>
          </AccordionSummary>

          <AccordionDetails sx={{ padding: "0rem" }}>
            <Box
              sx={{ display: "flex", color: "white", borderRadius: "8px !important" }}
              onClick={(e) => e?.stopPropagation()}
            >
              <DayPicker
                mode="multiple"
                classNames={DAY_PICKER_CLASSES}
                disabled={{
                  before: dayjs(session?.sessionStart).toDate(),
                  after: dayjs(session?.sessionEnd).toDate(),
                }}
                defaultMonth={dayjs(session?.sessionStart)?.toDate()}
                required
                selected={values?.calendarViewDates?.map((day) => dayjs(day).toDate())}
                onSelect={(dates) => setValues((prev) => applyCalendarSelection(prev, dates))}
              />

              <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem",
                    padding: "1rem",
                    flex: 1,
                  }}
                >
                  <Typography>Selected dates: </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      justifyContent: "flex-start",
                      gap: "0.5rem",
                      maxHeight: "200px",
                      overflowY: "auto",
                      "&::-webkit-scrollbar": { width: "7px" },
                    }}
                  >
                    {pickedRows.map((row, idx) => (
                      <SelectedDateChip
                        key={`${row.dateStart}-${idx}`}
                        row={row}
                        onRemove={() => confirmRemove(row)}
                      />
                    ))}
                  </Box>
                </Box>

                <Box
                  sx={{
                    padding: "1rem",
                    paddingTop: "0rem",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    gap: "10px",
                    width: "100%",
                    borderTop: "1px solid",
                    borderColor: pinkHex(),
                  }}
                >
                  <EachDateOfSessionCheckbox values={values} setValues={setValues} />

                  {rows.length > 0 && values?.calendarViewDates?.length > 0 ? (
                    <CalendarBulkFields
                      values={values}
                      setValues={setValues}
                      handleBlur={handleBlur}
                      session={session}
                    />
                  ) : null}
                </Box>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
};

export default CalendarDatesPanel;
